import React, { useState, useEffect } from 'react';
import { nanoid } from 'nanoid';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ElementProperties } from './ElementProperties';
import { DraggableElement } from './DraggableElement';
import { Separator } from '@/components/ui/separator';
import parse from 'html-react-parser';
import {
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight,
  Link, Image as ImageIcon, Type, Heading1, 
  Square, Columns, LayoutGrid, Save
} from 'lucide-react';

interface EmailEditorProps {
  initialContent: string;
  onSave: (content: string) => void;
}

// Initial default elements
const getInitialElements = () => [
  {
    id: nanoid(),
    type: 'heading',
    content: 'Welcome to our Newsletter',
    position: { x: 50, y: 50 },
    fontFamily: 'Arial, sans-serif',
    fontSize: 24,
    color: '#000000',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  {
    id: nanoid(),
    type: 'text',
    content: 'This is a sample text block. Edit or delete it and start creating your email.',
    position: { x: 50, y: 100 },
    fontFamily: 'Arial, sans-serif',
    fontSize: 16,
    color: '#333333',
    textAlign: 'left',
  }
];

export const EmailEditor: React.FC<EmailEditorProps> = ({ initialContent, onSave }) => {
  const [elements, setElements] = useState<any[]>(getInitialElements());
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [editorWidth, setEditorWidth] = useState(600);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [lastInitialContent, setLastInitialContent] = useState('');
  
  // Parse initial content if available
  useEffect(() => {
    if (initialContent && initialContent.trim() !== '' && initialContent !== lastInitialContent) {
      try {
        // Extract background color
        const bgColorMatch = initialContent.match(/background-color:\s*([^;]+);/);
        if (bgColorMatch && bgColorMatch[1]) {
          setBackgroundColor(bgColorMatch[1]);
        }
        
        // Extract container width
        const widthMatch = initialContent.match(/max-width:\s*(\d+)px/);
        if (widthMatch && widthMatch[1]) {
          setEditorWidth(parseInt(widthMatch[1]));
        }
        
        // Parse HTML to extract elements
        const newElements = parseHtmlToElements(initialContent);
        
        if (newElements.length > 0) {
          setElements(newElements);
          setLastInitialContent(initialContent);
        }
      } catch (error) {
        console.error('Failed to parse email content', error);
      }
    }
  }, [initialContent]);
  
  // Function to parse HTML into editor elements
  const parseHtmlToElements = (html: string): any[] => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const container = doc.querySelector('.container') || doc.body;
    const extractedElements: any[] = [];
    let yPosition = 50;
    
    // Extract background color and width from the document
    const bodyStyle = doc.body.getAttribute('style') || '';
    const bgColorMatch = bodyStyle.match(/background-color:\s*([^;]+);/) || 
                         html.match(/background-color:\s*([^;]+);/);
    if (bgColorMatch && bgColorMatch[1]) {
      setBackgroundColor(bgColorMatch[1].trim());
    }
    
    // Extract container width from style
    const containerStyle = container.getAttribute('style') || '';
    const containerWidthMatch = containerStyle.match(/max-width:\s*(\d+)px/) ||
                                html.match(/max-width:\s*(\d+)px/);
    if (containerWidthMatch && containerWidthMatch[1]) {
      setEditorWidth(parseInt(containerWidthMatch[1].trim()));
    }
    
    // Extract styles from <style> tag
    let styleRules: Record<string, Record<string, string>> = {};
    const styleTag = doc.querySelector('style');
    if (styleTag && styleTag.textContent) {
      const styleContent = styleTag.textContent;
      // Simple regex to extract class-based styles
      const styleRegex = /\.([^{]+){([^}]+)}/g;
      let match;
      while ((match = styleRegex.exec(styleContent)) !== null) {
        const className = match[1].trim();
        const styleBody = match[2].trim();
        
        if (!styleRules[className]) {
          styleRules[className] = {};
        }
        
        // Extract individual style properties
        const propertyRegex = /([^:]+):([^;]+);/g;
        let propertyMatch;
        while ((propertyMatch = propertyRegex.exec(styleBody)) !== null) {
          const property = propertyMatch[1].trim();
          const value = propertyMatch[2].trim();
          styleRules[className][property] = value;
        }
      }
    }
    
    // Function to get style for an element based on class and inline styles
    const getStyle = (element: Element, property: string, defaultValue: string): string => {
      // First check inline styles
      const inlineStyle = element.getAttribute('style');
      if (inlineStyle) {
        const inlineMatch = inlineStyle.match(new RegExp(`${property}\\s*:\\s*([^;]+)`, 'i'));
        if (inlineMatch && inlineMatch[1]) {
          return inlineMatch[1].trim();
        }
      }
      
      // Then check class-based styles
      for (const className of Array.from(element.classList)) {
        if (styleRules[className] && styleRules[className][property]) {
          return styleRules[className][property];
        }
      }
      
      // Finally check parent element styles for inheritance
      if (element.parentElement && element !== container) {
        return getStyle(element.parentElement, property, defaultValue);
      }
      
      return defaultValue;
    };
    
    // Find structural sections
    const header = doc.querySelector('.header') || doc.querySelector('header');
    const content = doc.querySelector('.content') || doc.querySelector('main');
    const footer = doc.querySelector('.footer') || doc.querySelector('footer');
    
    // Helper to calculate element height based on content and styling
    const estimateElementHeight = (element: Element): number => {
      const fontSize = parseInt(getStyle(element, 'font-size', '16').replace('px', ''));
      const lineHeight = parseFloat(getStyle(element, 'line-height', '1.5'));
      const paddingTop = parseInt(getStyle(element, 'padding-top', '0').replace('px', ''));
      const paddingBottom = parseInt(getStyle(element, 'padding-bottom', '0').replace('px', ''));
      const marginTop = parseInt(getStyle(element, 'margin-top', '0').replace('px', ''));
      const marginBottom = parseInt(getStyle(element, 'margin-bottom', '0').replace('px', ''));
      
      // Basic estimate for text content
      if (element.textContent) {
        const textLength = element.textContent.length;
        const linesEstimate = Math.max(1, Math.ceil(textLength / 50)); // Rough estimate: ~50 chars per line
        return (fontSize * lineHeight * linesEstimate) + paddingTop + paddingBottom + marginTop + marginBottom;
      }
      
      return 30; // Default minimum height
    };
    
    // Process all elements recursively, including nested ones
    const processElements = (parentElement: Element, startY: number, isStructuralElement = false): number => {
      let currentY = startY;
      let elements = Array.from(parentElement.children);
      
      // Direct text nodes in divs or containers should be wrapped in paragraph elements
      if (
        (parentElement.tagName.toLowerCase() === 'div' || parentElement === container) && 
        parentElement.childNodes.length > 0
      ) {
        const textNodes = Array.from(parentElement.childNodes)
          .filter(node => node.nodeType === Node.TEXT_NODE && node.textContent && node.textContent.trim());
          
        if (textNodes.length > 0) {
          textNodes.forEach(textNode => {
            if (textNode.textContent && textNode.textContent.trim()) {
              const element = {
                id: nanoid(),
                type: 'text',
                content: textNode.textContent.trim(),
                position: { x: 50, y: currentY },
                fontFamily: getStyle(parentElement, 'font-family', 'Arial, sans-serif'),
                fontSize: parseInt(getStyle(parentElement, 'font-size', '16').replace('px', '')) || 16,
                color: getStyle(parentElement, 'color', '#333333'),
                textAlign: getStyle(parentElement, 'text-align', 'left'),
                fontWeight: getStyle(parentElement, 'font-weight', 'normal'),
                fontStyle: getStyle(parentElement, 'font-style', 'normal'),
                textDecoration: getStyle(parentElement, 'text-decoration', 'none'),
                backgroundColor: getStyle(parentElement, 'background-color', 'transparent')
              };
              extractedElements.push(element);
              currentY += Math.max(60, estimateElementHeight(parentElement));
            }
          });
        }
      }
      
      for (const element of elements) {
        const style = window.getComputedStyle(element);
        const display = (element as HTMLElement).style?.display || style.display;
        // Skip hidden elements
        if (display === 'none') continue;
        
        // Calculate estimated height based on element content and styling
        const elementHeight = estimateElementHeight(element);
        
        let elementObj: any = null;
        
        switch (element.tagName.toLowerCase()) {
          case 'h1':
          case 'h2':
          case 'h3':
          case 'h4':
          case 'h5':
          case 'h6':
            elementObj = {
              id: nanoid(),
              type: 'heading',
              content: element.textContent || 'Heading',
              position: { x: 50, y: currentY },
              fontFamily: getStyle(element, 'font-family', 'Arial, sans-serif'),
              fontSize: parseInt(getStyle(element, 'font-size', '24').replace('px', '')) || 24,
              color: getStyle(element, 'color', '#000000'),
              textAlign: getStyle(element, 'text-align', 'left'),
              fontWeight: getStyle(element, 'font-weight', 'bold'),
              fontStyle: getStyle(element, 'font-style', 'normal'),
              textDecoration: getStyle(element, 'text-decoration', 'none'),
              backgroundColor: getStyle(element, 'background-color', 'transparent')
            };
            break;
            
          case 'p':
            elementObj = {
              id: nanoid(),
              type: 'text',
              content: element.textContent || 'Text',
              position: { x: 50, y: currentY },
              fontFamily: getStyle(element, 'font-family', 'Arial, sans-serif'),
              fontSize: parseInt(getStyle(element, 'font-size', '16').replace('px', '')) || 16,
              color: getStyle(element, 'color', '#333333'),
              textAlign: getStyle(element, 'text-align', 'left'),
              fontWeight: getStyle(element, 'font-weight', 'normal'),
              fontStyle: getStyle(element, 'font-style', 'normal'),
              textDecoration: getStyle(element, 'text-decoration', 'none'),
              backgroundColor: getStyle(element, 'background-color', 'transparent')
            };
            break;
            
          case 'img':
            elementObj = {
              id: nanoid(),
              type: 'image',
              position: { x: 50, y: currentY },
              src: element.getAttribute('src') || 'https://placehold.co/600x400',
              alt: element.getAttribute('alt') || 'Image',
              width: parseInt(element.getAttribute('width') || '0') || 
                    parseInt(getStyle(element, 'width', '600').replace('px', '')) || 600,
              height: parseInt(element.getAttribute('height') || '0') || 
                     parseInt(getStyle(element, 'height', '400').replace('px', '')) || 400,
              borderRadius: parseInt(getStyle(element, 'border-radius', '0').replace('px', '')) || 0
            };
            break;
            
          case 'a':
            // Check if it looks like a button
            if (
              element.classList.contains('button') || 
              element.classList.contains('btn') ||
              getStyle(element, 'display', '') === 'inline-block' ||
              getStyle(element, 'background-color', '') !== 'transparent' ||
              element.getAttribute('role') === 'button'
            ) {
              elementObj = {
                id: nanoid(),
                type: 'button',
                text: element.textContent || 'Button',
                position: { x: 50, y: currentY },
                href: element.getAttribute('href') || '#',
                backgroundColor: getStyle(element, 'background-color', '#4F46E5'),
                color: getStyle(element, 'color', '#FFFFFF'),
                fontFamily: getStyle(element, 'font-family', 'Arial, sans-serif'),
                fontSize: parseInt(getStyle(element, 'font-size', '16').replace('px', '')) || 16,
                textAlign: getStyle(element, 'text-align', 'center'),
                borderRadius: parseInt(getStyle(element, 'border-radius', '4').replace('px', '')) || 4,
                paddingX: parseInt(getStyle(element, 'padding-left', '16').replace('px', '')) || 16,
                paddingY: parseInt(getStyle(element, 'padding-top', '8').replace('px', '')) || 8,
                style: getStyle(element, 'background-color', '') === 'transparent' ? 'outline' : 'filled'
              };
            } else {
              // Handle as normal text with a link
              elementObj = {
                id: nanoid(),
                type: 'text',
                content: element.textContent || 'Link',
                position: { x: 50, y: currentY },
                fontFamily: getStyle(element, 'font-family', 'Arial, sans-serif'),
                fontSize: parseInt(getStyle(element, 'font-size', '16').replace('px', '')) || 16,
                color: getStyle(element, 'color', '#0000FF'),
                textAlign: getStyle(element, 'text-align', 'left'),
                fontWeight: getStyle(element, 'font-weight', 'normal'),
                fontStyle: getStyle(element, 'font-style', 'normal'),
                textDecoration: 'underline',
                link: element.getAttribute('href') || '#'
              };
            }
            break;
            
          case 'hr':
            elementObj = {
              id: nanoid(),
              type: 'divider',
              position: { x: 50, y: currentY },
              color: getStyle(element, 'background-color', '#DDDDDD') || 
                     getStyle(element, 'border-color', '#DDDDDD'),
              width: parseInt(getStyle(element, 'width', '100%').replace('%', '')) || 100
            };
            break;
            
          case 'div':
            // Special handling for footer and header divs
            if (
              element.classList.contains('footer') || 
              element.id === 'footer' || 
              element.tagName.toLowerCase() === 'footer'
            ) {
              // Process footer separately to ensure it's positioned correctly
              const footerY = processElements(element, currentY, true);
              return footerY; // Return after processing the footer
            }
            
            // Check if it's a spacer div (empty div with height)
            if (!element.textContent?.trim() && element.children.length === 0) {
              const heightStyle = getStyle(element, 'height', '');
              if (heightStyle && heightStyle !== '0' && heightStyle !== '0px') {
                elementObj = {
                  id: nanoid(),
                  type: 'spacer',
                  position: { x: 50, y: currentY },
                  height: parseInt(heightStyle.replace('px', '')) || 50,
                };
                break;
              }
            }
            
            // If div has a distinct background color or border, make it a container element
            const bgColor = getStyle(element, 'background-color', 'transparent');
            const borderWidth = getStyle(element, 'border-width', '0');
            const hasBorder = borderWidth !== '0' && borderWidth !== '0px';
            const hasBackground = bgColor !== 'transparent';
            
            if ((hasBackground || hasBorder) && element.children.length > 0) {
              // Create a container-like visual representation using a text element with background
              elementObj = {
                id: nanoid(),
                type: 'text',
                content: element.textContent || ' ',
                position: { x: 50, y: currentY },
                fontFamily: getStyle(element, 'font-family', 'Arial, sans-serif'),
                fontSize: parseInt(getStyle(element, 'font-size', '16').replace('px', '')) || 16,
                color: getStyle(element, 'color', '#333333'),
                textAlign: getStyle(element, 'text-align', 'left'),
                backgroundColor: bgColor,
                padding: parseInt(getStyle(element, 'padding', '0').replace('px', '')) || 0,
                borderRadius: parseInt(getStyle(element, 'border-radius', '0').replace('px', '')) || 0
              };
              
              // Since we're representing this div as a container, don't process its children separately
              break;
            }
            
            // Otherwise, recursively process div's children
            const childY = processElements(element, currentY);
            currentY = childY; // Update current Y position based on child elements
            continue; // Skip the normal element addition process
            
          case 'ul':
          case 'ol':
            // Represent lists as text elements
            const listItems = Array.from(element.querySelectorAll('li')).map(li => li.textContent);
            const listPrefix = element.tagName.toLowerCase() === 'ul' ? '• ' : '';
            const listContent = listItems.map((item, index) => 
              element.tagName.toLowerCase() === 'ul' 
                ? `${listPrefix}${item}` 
                : `${index + 1}. ${item}`
            ).join('\n');
            
            elementObj = {
              id: nanoid(),
              type: 'text',
              content: listContent,
              position: { x: 50, y: currentY },
              fontFamily: getStyle(element, 'font-family', 'Arial, sans-serif'),
              fontSize: parseInt(getStyle(element, 'font-size', '16').replace('px', '')) || 16,
              color: getStyle(element, 'color', '#333333'),
              textAlign: getStyle(element, 'text-align', 'left'),
              fontWeight: getStyle(element, 'font-weight', 'normal'),
              backgroundColor: getStyle(element, 'background-color', 'transparent')
            };
            break;
            
          // Specifically handle footer elements
          case 'footer':
            // Process the footer contents
            const footerElements = processElements(element, currentY, true);
            return footerElements;
            
          default:
            // For any other elements, check if they have text content
            if (element.textContent?.trim() && !['script', 'style', 'meta', 'link'].includes(element.tagName.toLowerCase())) {
              elementObj = {
                id: nanoid(),
                type: 'text',
                content: element.textContent.trim(),
                position: { x: 50, y: currentY },
                fontFamily: getStyle(element, 'font-family', 'Arial, sans-serif'),
                fontSize: parseInt(getStyle(element, 'font-size', '16').replace('px', '')) || 16,
                color: getStyle(element, 'color', '#333333'),
                textAlign: getStyle(element, 'text-align', 'left'),
                backgroundColor: getStyle(element, 'background-color', 'transparent')
              };
            } else if (element.children.length > 0) {
              // Process children for elements we don't directly handle
              const childY = processElements(element, currentY);
              currentY = childY;
              continue;
            } else {
              continue; // Skip this element
            }
        }
        
        if (elementObj) {
          extractedElements.push(elementObj);
          // Use dynamic spacing based on element type and content
          const spacing = elementObj.type === 'heading' ? 100 : 
                          elementObj.type === 'image' ? (elementObj.height / 4) + 60 : 
                          Math.max(60, elementHeight);
          currentY += spacing;
        }
      }
      
      return currentY;
    };
    
    // Process structural sections in the correct order
    let maxY = yPosition;
    
    // Process header if it exists
    if (header) {
      maxY = processElements(header, maxY);
      maxY += 50; // Add extra spacing after header
    }
    
    // Process main content
    if (content) {
      maxY = processElements(content, maxY);
      maxY += 50; // Add extra spacing after content
    } else {
      // If no specific content container, process body or main container
      maxY = processElements(container, maxY);
    }
    
    // Process footer last to ensure it's at the bottom
    if (footer) {
      maxY = processElements(footer, maxY);
    }
    
    // If we couldn't extract any elements, fallback to default
    return extractedElements.length > 0 ? extractedElements : getInitialElements();
  };
  
  const selectedElement = elements.find(el => el.id === selectedElementId);
  
  const addElement = (type: string) => {
    const newElement = {
      id: nanoid(),
      type,
      position: { x: 50, y: Math.max(...elements.map(el => el.position.y + 100), 100) },
    };
    
    // Add default properties based on element type
    switch (type) {
      case 'heading':
        Object.assign(newElement, {
          content: 'Heading',
          fontFamily: 'Arial, sans-serif',
          fontSize: 24,
          color: '#000000',
          fontWeight: 'bold',
          textAlign: 'center',
        });
        break;
      case 'text':
        Object.assign(newElement, {
          content: 'Text block',
          fontFamily: 'Arial, sans-serif',
          fontSize: 16,
          color: '#333333',
          textAlign: 'left',
        });
        break;
      case 'image':
        Object.assign(newElement, {
          src: 'https://placehold.co/600x400',
          alt: 'Image',
          width: 600,
          height: 400,
        });
        break;
      case 'button':
        Object.assign(newElement, {
          text: 'Click Me',
          href: '#',
          backgroundColor: '#4F46E5',
          color: '#FFFFFF',
          borderRadius: 4,
          paddingX: 16,
          paddingY: 8,
          textAlign: 'center',
          fontFamily: 'Arial, sans-serif',
          fontSize: 16,
          style: 'filled',
        });
        break;
      case 'divider':
        Object.assign(newElement, {
          width: editorWidth - 100,
          height: 1,
          color: '#DDDDDD',
        });
        break;
      case 'spacer':
        Object.assign(newElement, {
          height: 50,
          width: editorWidth - 100,
        });
        break;
    }
    
    setElements([...elements, newElement]);
    setSelectedElementId(newElement.id);
  };
  
  const updateElement = (id: string, properties: any) => {
    setElements(elements.map(el => 
      el.id === id ? { ...el, ...properties } : el
    ));
  };
  
  const deleteElement = (id: string) => {
    setElements(elements.filter(el => el.id !== id));
    if (selectedElementId === id) {
      setSelectedElementId(null);
    }
  };
  
  const duplicateElement = (id: string) => {
    const element = elements.find(el => el.id === id);
    if (element) {
      const newElement = {
        ...element,
        id: nanoid(),
        position: {
          x: element.position.x + 20,
          y: element.position.y + 20
        }
      };
      setElements([...elements, newElement]);
      setSelectedElementId(newElement.id);
    }
  };
  
  const handleSave = () => {
    // Convert elements to HTML
    // This would be more complex in a real implementation
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Email</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            background-color: ${backgroundColor};
            margin: 0;
            padding: 0;
          }
          .container {
            max-width: ${editorWidth}px;
            margin: 0 auto;
            padding: 20px;
          }
          ${elements.map(el => {
            switch (el.type) {
              case 'heading':
              case 'text':
                return `
                  .element-${el.id} {
                    font-family: ${el.fontFamily || 'Arial, sans-serif'};
                    font-size: ${el.fontSize || 16}px;
                    color: ${el.color || '#000000'};
                    text-align: ${el.textAlign || 'left'};
                    font-weight: ${el.fontWeight || 'normal'};
                    font-style: ${el.fontStyle || 'normal'};
                    text-decoration: ${el.textDecoration || 'none'};
                    ${el.backgroundColor ? `background-color: ${el.backgroundColor};` : ''}
                    margin: 0;
                    padding: 10px;
                  }
                `;
              case 'image':
                return `
                  .element-${el.id} {
                    max-width: 100%;
                    height: auto;
                    border-radius: ${el.borderRadius || 0}px;
                  }
                `;
              case 'button':
                return `
                  .element-${el.id} {
                    display: inline-block;
                    background-color: ${el.backgroundColor || '#4F46E5'};
                    color: ${el.color || '#FFFFFF'};
                    padding: ${el.paddingY || 8}px ${el.paddingX || 16}px;
                    border-radius: ${el.borderRadius || 4}px;
                    text-decoration: none;
                    font-family: ${el.fontFamily || 'Arial, sans-serif'};
                    font-size: ${el.fontSize || 16}px;
                    text-align: center;
                    ${el.style === 'outline' ? `
                      background-color: transparent;
                      border: 1px solid ${el.backgroundColor || '#4F46E5'};
                    ` : ''}
                    ${el.style === 'ghost' ? `
                      background-color: transparent;
                      border: none;
                    ` : ''}
                  }
                `;
              default:
                return '';
            }
          }).join('\n')}
        </style>
      </head>
      <body>
        <div class="container">
          ${elements.map(el => {
            switch (el.type) {
              case 'heading':
                return `<h2 class="element-${el.id}">${el.content || 'Heading'}</h2>`;
              case 'text':
                return `<p class="element-${el.id}">${el.content || 'Text'}</p>`;
              case 'image':
                return `
                  ${el.link ? `<a href="${el.link}" target="_blank">` : ''}
                  <img 
                    src="${el.src || ''}" 
                    alt="${el.alt || ''}" 
                    class="element-${el.id}"
                    ${el.width ? `width="${el.width}"` : ''}
                    ${el.height ? `height="${el.height}"` : ''}
                  />
                  ${el.link ? `</a>` : ''}
                `;
              case 'button':
                return `<a href="${el.href || '#'}" class="element-${el.id}">${el.text || 'Button'}</a>`;
              case 'divider':
                return `<hr style="border: none; height: 1px; background-color: ${el.color || '#DDDDDD'}; margin: 20px 0;" />`;
              case 'spacer':
                return `<div style="height: ${el.height || 50}px;"></div>`;
              default:
                return '';
            }
          }).join('\n')}
        </div>
      </body>
      </html>
    `;
    
    onSave(htmlContent);
  };
  
  const renderElementContent = (element: any) => {
    switch (element.type) {
      case 'heading':
        return (
          <h2 
            style={{ 
              fontFamily: element.fontFamily,
              fontSize: `${element.fontSize}px`,
              color: element.color,
              textAlign: element.textAlign,
              fontWeight: element.fontWeight,
              fontStyle: element.fontStyle,
              textDecoration: element.textDecoration,
              backgroundColor: element.backgroundColor || 'transparent',
              margin: 0,
              padding: '10px',
            }}
          >
            {element.content}
          </h2>
        );
      
      case 'text':
        return (
          <p 
            style={{ 
              fontFamily: element.fontFamily,
              fontSize: `${element.fontSize}px`,
              color: element.color,
              textAlign: element.textAlign,
              fontWeight: element.fontWeight,
              fontStyle: element.fontStyle,
              textDecoration: element.textDecoration,
              backgroundColor: element.backgroundColor || 'transparent',
              margin: 0,
              padding: '10px',
            }}
          >
            {element.content}
          </p>
        );
      
      case 'image':
        return (
          <img
            src={element.src}
            alt={element.alt}
            style={{
              maxWidth: '100%',
              borderRadius: `${element.borderRadius || 0}px`,
              width: element.width ? `${element.width}px` : 'auto',
              height: element.height ? `${element.height}px` : 'auto',
            }}
          />
        );
      
      case 'button':
        return (
          <button
            style={{
              backgroundColor: element.style === 'filled' ? element.backgroundColor : 
                              element.style === 'outline' ? 'transparent' : 'transparent',
              color: element.color,
              border: element.style === 'outline' ? `1px solid ${element.backgroundColor}` : 
                     element.style === 'ghost' ? 'none' : 'none',
              borderRadius: `${element.borderRadius || 4}px`,
              padding: `${element.paddingY || 8}px ${element.paddingX || 16}px`,
              fontSize: `${element.fontSize || 16}px`,
              fontFamily: element.fontFamily || 'Arial, sans-serif',
              cursor: 'pointer',
              textAlign: 'center',
            }}
          >
            {element.text || 'Button'}
          </button>
        );
      
      case 'divider':
        return (
          <hr
            style={{
              border: 'none',
              height: '1px',
              backgroundColor: element.color || '#DDDDDD',
              width: '100%',
              margin: '10px 0',
            }}
          />
        );
      
      case 'spacer':
        return (
          <div
            style={{
              height: `${element.height || 50}px`,
              width: '100%',
            }}
          />
        );
      
      default:
        return <div>Unknown element type</div>;
    }
  };
  
  return (
    <div className="grid grid-cols-4 gap-4 h-full">
      {/* Left sidebar - Element Library */}
      <div className="col-span-1 border rounded-lg p-4 bg-background">
        <h3 className="font-medium mb-4">Elements</h3>
        <ScrollArea className="h-[calc(100vh-280px)]">
          <div className="space-y-2">
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2" 
              onClick={() => addElement('heading')}
            >
              <Heading1 className="h-4 w-4" />
              Heading
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2" 
              onClick={() => addElement('text')}
            >
              <Type className="h-4 w-4" />
              Text
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2" 
              onClick={() => addElement('image')}
            >
              <ImageIcon className="h-4 w-4" />
              Image
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2" 
              onClick={() => addElement('button')}
            >
              <Square className="h-4 w-4" />
              Button
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2" 
              onClick={() => addElement('divider')}
            >
              <Separator className="h-4 w-4" />
              Divider
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2" 
              onClick={() => addElement('spacer')}
            >
              <LayoutGrid className="h-4 w-4" />
              Spacer
            </Button>
          </div>
        </ScrollArea>
      </div>
      
      {/* Middle - Canvas */}
      <div className="col-span-2 border rounded-lg overflow-hidden bg-muted/30 flex flex-col">
        <div className="p-2 bg-background border-b flex gap-2 items-center justify-between">
          <div className="flex gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <Bold className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <Italic className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <Underline className="h-4 w-4" />
            </Button>
            <Separator orientation="vertical" className="mx-1 h-6" />
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <AlignLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <AlignCenter className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <AlignRight className="h-4 w-4" />
            </Button>
          </div>
          
          <Button onClick={handleSave} size="sm" className="gap-1">
            <Save className="h-4 w-4" />
            Save
          </Button>
        </div>
        
        <div className="flex-1 overflow-auto p-8">
          <div 
            className="bg-white mx-auto shadow-sm relative"
            style={{ 
              width: `${editorWidth}px`, 
              minHeight: '800px',
              backgroundColor: backgroundColor
            }}
          >
            {elements.map((element) => (
              <DraggableElement
                key={element.id}
                id={element.id}
                type={element.type}
                content={renderElementContent(element)}
                position={element.position}
                onDelete={deleteElement}
                onSelect={setSelectedElementId}
                onDuplicate={duplicateElement}
                onPositionChange={(id, position) => updateElement(id, { position })}
                isSelected={selectedElementId === element.id}
              />
            ))}
          </div>
        </div>
      </div>
      
      {/* Right sidebar - Element Properties */}
      <div className="col-span-1 border rounded-lg p-4 bg-background">
        <h3 className="font-medium mb-4">Properties</h3>
        <ScrollArea className="h-[calc(100vh-280px)]">
          {selectedElement ? (
            <ElementProperties
              selectedElement={selectedElement}
              onUpdateElement={updateElement}
            />
          ) : (
            <div className="text-center text-muted-foreground p-4">
              Select an element to edit its properties
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}; 